/**
 * discountAttribution.ts — Atribución de descuentos de línea para la
 * presentación de Totales (web, térmica y panel de cobro).
 *
 * Modelo de la línea (unitaria). Los descuentos de precio son INDEPENDIENTES y
 * se apilan en orden, cada uno con su propia identidad (NUNCA se fusionan):
 *
 *   L  = precio de lista (precioLista ?? precio)
 *   W  = precio mayorista (activo solo si esMayorista y W < L); si no, W = L
 *   V  = precio tras VIP = W × (1 − vipDescuentoPct/100), si el nivel aplica
 *   N  = precio cobrado = V tras el ajuste manual por ítem (lineAdjustment)
 *
 * Cada paso genera su propia fila de descuento (por línea y en Totales):
 *   mayorista_i = (L − W) × qty    → «Descuento mayorista»
 *   vip_i       = (W − V) × qty    → «Descuento VIP»
 *   items_i     = (V − N) × qty    → «Descuentos por ítem»
 *
 * Invariante: listaTotal − mayorista − vip − items ≈ cobradoTotal (redondeos
 * por paso aparte). Los descuentos NUNCA suben el precio de la línea.
 *
 * Módulo puro: no UI, no storage. Fuente única para que el ticket web, la
 * térmica y el resumen del cobro cuenten la MISMA historia con las MISMAS
 * filas y montos, sin doble conteo ni mezcla de conceptos.
 */
import type { CartItem } from "@/types";
import type { LineAdjustment, LineDiscount } from "./discountHelper";
import { getLineListaTotal, getLineUnitLista } from "@/lib/cartLineTotals";

export { getLineListaTotal, getLineUnitLista } from "@/lib/cartLineTotals";

const clampMoney = (value: number): number => Math.max(0, Math.round(value));
const clampPct = (value: number): number =>
  Math.min(100, Math.max(0, Math.round(value)));

/**
 * Precio base tras mayorista por unidad (lista si el mayorista no está activo
 * o no baja el precio). Independiente del VIP.
 */
export function getLineMayoristaUnit(item: CartItem): number {
  if (item.peso && item.peso > 0) return getLineUnitLista(item);
  const lista = getLineUnitLista(item);
  if (
    item.esMayorista === true &&
    typeof item.precioMayorista === "number" &&
    item.precioMayorista > 0 &&
    item.precioMayorista < lista
  ) {
    return item.precioMayorista;
  }
  return lista;
}

/** % de descuento VIP (del nivel del cliente) aplicable a la línea. */
export function getLineVipPct(item: CartItem): number {
  if (item.peso && item.peso > 0) return 0;
  if (item.esVIP !== true) return 0;
  return clampPct(item.vipDescuentoPct ?? 0);
}

/** ¿La línea tiene un precio especial (mayorista y/o VIP) activo? */
export function lineTieneEspecial(item: CartItem): boolean {
  if (item.peso && item.peso > 0) return false;
  return (
    getLineMayoristaUnit(item) < getLineUnitLista(item) ||
    getLineVipPct(item) > 0
  );
}

/**
 * Precio base efectivo por unidad tras la cadena lista→mayorista→VIP (sin el
 * ajuste manual por ítem). Es la base sobre la que se apila el descuento % por
 * ítem del modal.
 */
export const getLineEspecialUnit = (item: CartItem): number => {
  if (item.peso && item.peso > 0) return getLineUnitLista(item);
  const base = getLineMayoristaUnit(item);
  const vipPct = getLineVipPct(item);
  if (vipPct > 0) return clampMoney(base * (1 - vipPct / 100));
  return clampMoney(base);
};

/**
 * Base sobre la que se apila el descuento % del modal de ítem: el precio
 * efectivo actual de la línea (mayorista→VIP si están activos, lista si no).
 */
export const getLineBaseAdjustUnit = (item: CartItem): number =>
  getLineEspecialUnit(item);

/**
 * Aplica el ajuste de línea sobre una base:
 *  - discountPercent: % apilado sobre la base (la base ya puede ser el precio
 *    tras mayorista/VIP).
 *  - manualPrice / useCostPrice: valor fijo del vendedor; si hay precio
 *    especial activo gana el MENOR (el precio de la línea nunca sube al
 *    activar mayorista/VIP).
 */
export const applyLineAdjustmentOver = (
  baseUnit: number,
  adjustment: LineAdjustment | null | undefined,
  especialActivo: boolean,
): number => {
  const base = clampMoney(baseUnit);
  if (!adjustment) return base;
  if (adjustment.mode === "discountPercent") {
    const pct = Math.max(0, adjustment.discountPercent ?? 0);
    return clampMoney(base * (1 - Math.min(100, pct) / 100));
  }
  if (adjustment.mode === "manualPrice") {
    const manual = Math.max(0, adjustment.manualUnitPrice ?? 0);
    if (manual <= 0) return base;
    return especialActivo ? Math.min(base, manual) : manual;
  }
  const costo = Math.max(0, adjustment.costUnitPrice ?? 0);
  if (costo <= 0) return base;
  return especialActivo ? Math.min(base, costo) : costo;
};

/** Precio unitario cobrado de la línea según el modelo (peso → total por línea). */
export const resolveLineCobradoUnit = (item: CartItem): number => {
  if (item.peso && item.peso > 0) return clampMoney(item.precio);
  const base = getLineEspecialUnit(item);
  return applyLineAdjustmentOver(
    base,
    item.lineAdjustment ?? null,
    lineTieneEspecial(item),
  );
};

/** Total cobrado real de la línea (fuente de verdad del carrito). */
export const getLineCobradoTotal = (item: CartItem): number => {
  if (item.peso && item.peso > 0) return clampMoney(item.precio);
  const qty = Math.max(1, item.quantity || 1);
  return clampMoney(resolveLineCobradoUnit(item) * qty);
};

export interface LineDiscountAttribution {
  /** Precio de lista (unitario × qty). */
  listaTotal: number;
  /** Ahorro por precio mayorista (L − W) × qty. */
  descuentoMayorista: number;
  /** Ahorro por descuento VIP (W − V) × qty. */
  descuentoVIP: number;
  /** Ahorro por descuentos manuales/catálogo por ítem (V − N) × qty. */
  descuentoApilado: number;
  /** Total cobrado real de la línea. */
  cobradoTotal: number;
}

/** Atribución de una línea del carrito (peso o unitaria). */
export function attribucionLinea(item: CartItem): LineDiscountAttribution {
  const cobradoTotal = getLineCobradoTotal(item);
  if (item.peso && item.peso > 0) {
    const listaTotal = getLineListaTotal(item);
    const apilado = Math.max(0, listaTotal - cobradoTotal);
    return {
      listaTotal,
      descuentoMayorista: 0,
      descuentoVIP: 0,
      descuentoApilado: apilado,
      cobradoTotal,
    };
  }

  const qty = Math.max(1, item.quantity || 1);
  const lista = getLineUnitLista(item);
  const cobradoUnit = clampMoney(item.precio ?? 0);
  // Si el cobrado supera la lista (precio manual por encima), se neutraliza la
  // atribución: la línea no genera filas de descuento negativas.
  if (cobradoUnit >= lista) {
    return {
      listaTotal: clampMoney(cobradoUnit * qty),
      descuentoMayorista: 0,
      descuentoVIP: 0,
      descuentoApilado: 0,
      cobradoTotal,
    };
  }

  const mayoristaUnit = getLineMayoristaUnit(item);
  const descMayoristaUnit =
    mayoristaUnit < lista ? Math.round(lista - mayoristaUnit) : 0;

  const vipPct = getLineVipPct(item);
  const vipUnit =
    vipPct > 0 ? clampMoney(mayoristaUnit * (1 - vipPct / 100)) : mayoristaUnit;
  const descVipUnit = vipPct > 0 ? Math.round(mayoristaUnit - vipUnit) : 0;

  const descApiladoUnit = Math.max(0, Math.round(vipUnit - cobradoUnit));

  return {
    listaTotal: clampMoney(lista * qty),
    descuentoMayorista: clampMoney(descMayoristaUnit * qty),
    descuentoVIP: clampMoney(descVipUnit * qty),
    descuentoApilado: clampMoney(descApiladoUnit * qty),
    cobradoTotal,
  };
}

export interface CartDiscountAttribution {
  subtotalLista: number;
  descuentoMayorista: number;
  descuentoVIP: number;
  descuentoApilado: number;
  subtotalItems: number;
  hasMayorista: boolean;
  hasVIP: boolean;
  hasApilado: boolean;
  hasLineDiscounts: boolean;
}

/** Atribución agregada del carrito (fuente única para Totales). */
export function atribucionCarrito(cart: CartItem[]): CartDiscountAttribution {
  let subtotalLista = 0;
  let descuentoMayorista = 0;
  let descuentoVIP = 0;
  let descuentoApilado = 0;
  let subtotalItems = 0;
  for (const item of Array.isArray(cart) ? cart : []) {
    const line = attribucionLinea(item);
    subtotalLista += line.listaTotal;
    descuentoMayorista += line.descuentoMayorista;
    descuentoVIP += line.descuentoVIP;
    descuentoApilado += line.descuentoApilado;
    subtotalItems += line.cobradoTotal;
  }
  const mayorista = clampMoney(descuentoMayorista);
  const vip = clampMoney(descuentoVIP);
  const apilado = clampMoney(descuentoApilado);
  const items = clampMoney(subtotalItems);
  return {
    subtotalLista: clampMoney(subtotalLista),
    descuentoMayorista: mayorista,
    descuentoVIP: vip,
    descuentoApilado: apilado,
    subtotalItems: items,
    hasMayorista: mayorista > 0,
    hasVIP: vip > 0,
    hasApilado: apilado > 0,
    hasLineDiscounts: mayorista > 0 || vip > 0 || apilado > 0,
  };
}

export interface TotalesConcepto {
  id: "base" | "mayorista" | "vip" | "items" | "global";
  label: string;
  amount: number;
}

export interface TotalesConceptos {
  rows: TotalesConcepto[];
  /** «Subtotal (precio lista)» cuando hay descuentos de línea; «Subtotal» si no. */
  baseLabel: string;
  subtotalLista: number;
  descuentoMayorista: number;
  descuentoVIP: number;
  descuentoApilado: number;
  subtotalItems: number;
}

export function globalDiscountLabel(discount: LineDiscount | null): string {
  if (discount && discount.type === "%" && discount.value > 0) {
    return `Descuento General (${Math.round(discount.value)}%)`;
  }
  return "Descuento General";
}

/**
 * Filas de descuento que deben salir IDÉNTICAS en el ticket web, la térmica y
 * el resumen del cobro. Cada concepto (mayorista, VIP, ítem, global) aparece
 * en su propia fila y la suma vertical cierra.
 */
export function buildTotalesConceptos(
  cart: CartItem[],
  breakdown: { descuentoGlobal: number },
  globalDiscount: LineDiscount | null,
): TotalesConceptos {
  const attr = atribucionCarrito(cart);
  const rows: TotalesConcepto[] = [];

  rows.push({
    id: "base",
    label: attr.hasLineDiscounts ? "Subtotal (precio lista)" : "Subtotal",
    amount: attr.subtotalLista,
  });
  if (attr.hasMayorista) {
    rows.push({
      id: "mayorista",
      label: "Descuento mayorista",
      amount: attr.descuentoMayorista,
    });
  }
  if (attr.hasVIP) {
    rows.push({
      id: "vip",
      label: "Descuento cliente VIP",
      amount: attr.descuentoVIP,
    });
  }
  if (attr.hasApilado) {
    rows.push({
      id: "items",
      label: "Descuentos por ítem",
      amount: attr.descuentoApilado,
    });
  }
  if (breakdown.descuentoGlobal > 0) {
    rows.push({
      id: "global",
      label: globalDiscountLabel(globalDiscount),
      amount: breakdown.descuentoGlobal,
    });
  }

  return {
    rows,
    baseLabel: rows[0]?.label ?? "Subtotal",
    subtotalLista: attr.subtotalLista,
    descuentoMayorista: attr.descuentoMayorista,
    descuentoVIP: attr.descuentoVIP,
    descuentoApilado: attr.descuentoApilado,
    subtotalItems: attr.subtotalItems,
  };
}
